import React, { useState, forwardRef, useImperativeHandle, Ref, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import AuthService from "@/services/auth";

// 表单数据类型定义
const userFormSchema = z.object({
  nickname: z.string().min(2, "昵称至少2个字符"),
  lang: z.string().min(1, "请选择语言"),
  password: z
    .string()
    .min(6, "密码至少 6 个字符")
    .max(30, "密码最长 30 个字符")
    .regex(/^(?=.*[a-zA-Z])(?=.*\d).{6,30}$/, "密码需包含字母和数字，长度 6-30 字符"),
});

type UserFormValues = z.infer<typeof userFormSchema>;

// AuthService.clearTokens();
// 组件属性定义
interface AddUserDialogProps {
  onSuccess?: (response: any) => void;
}

// 暴露给父组件的方法类型
interface DialogRef {
  open: () => void;
  close: () => void;
}

export const AddUserDialog = forwardRef<DialogRef, AddUserDialogProps>(({ onSuccess }, ref) => {
  const baseUrl = window.location.origin + window.location.pathname + "#/";
  const [isOpen, setIsOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const url = window.location.hash;
  const hash = url.split("#")[1];
  const token = hash.split("/registration/")[1];
  const [inviterInfo, setInviterInfo] = useState({
    inviterName: undefined,
    nickName: undefined,
    teamName: undefined,
    userEmail: undefined,
    teamId: undefined,
  });
  const navigate = useNavigate();
  // 表单配置
  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      nickname: "",
      lang: "",
      password: "",
    },
  });

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    open: () => {
      setIsOpen(true);
      form.reset();
      setErrorMessage("");
    },
    close: () => setIsOpen(false),
  }));

  // 获取邀请者的信息
  const getInFo = async () => {
    try {
      // const token = getTokenFromUrl();
      console.log(token);
      if (!token) {
        setErrorMessage("未找到有效的token");
        return;
      }
      // return false

      const response: any = await api.get("/v1/user/invite/info", {
        params: { token },
      });
      console.log(response);
      if (response.userEmail) {
        setInviterInfo({ ...response });
        form.reset({
          nickname: response.nickName || "",
          lang: "ZH_CN",
          password: "",
        });
      } else {
        setErrorMessage("获取邀请人信息失败");
      }
    } catch (err) {
      console.log(err.data);
      const code = (err as any)?.data?.err || (err as any)?.code || (err as any)?.data?.code;
      const msg = (err as any)?.data?.msg || (err as any)?.message || "获取邀请人信息失败，请稍后重试";
      setErrorCode(code || null);
      setErrorMessage(msg);
    }
  };

  useEffect(() => {
    getInFo();
  }, []);

  // 处理表单提交
  const onSubmit = async (data: UserFormValues) => {
    try {
      // const token = getTokenFromUrl();
      if (!token) {
        setErrorMessage("未找到有效的token");
        return;
      }

      setIsSubmitting(true);
      setErrorMessage("");

      const response: any = await api.get("/v1/user/account/active", {
        params: { token, ...data },
      });

      //onSuccess?.(response);
      toast.success("激活成功");
      setSuccessMessage("激活成功!");
    } catch (error) {
      const code = (error as any)?.data?.err || (error as any)?.code || (error as any)?.data?.code;
      const msg = (error as any)?.data?.msg || (error as any)?.message || "网络异常，请稍后重试";
      setErrorCode(code || null);
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // 阻止背景点击和ESC关闭
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
    >
      <DialogContent
        className="sm:max-w-[70vw] max-h-[70vh] overflow-y-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        style={{
          width: "70vw",
          height: "auto",
          maxWidth: "500px",
          maxHeight: "70vh",
          overflow: "auto",
        }}
      >
        <DialogHeader>
          <DialogTitle>填寫激活信息</DialogTitle>
          <DialogDescription>请填寫激活信息，帶 * 的為必填項</DialogDescription>
        </DialogHeader>

        {errorMessage && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">{errorMessage}</div>}

        {/* 邀请人信息展示（只读） */}
        {inviterInfo && errorCode !== "client.bad.invite.token" && errorCode !== "client.user.already.active" && (
          <div className="mb-6 p-4 bg-gray-50 rounded-md">
            <h4 className="text-sm font-medium text-gray-500 mb-2">
              {inviterInfo.inviterName} 邀请您加入 {inviterInfo.teamName}
            </h4>
            {/* <div className="space-y-2">

                                <div className="flex">
                                    <span className="w-24 text-gray-500">邀請者:</span>
                                    <span className="text-gray-900">{inviterInfo.inviterName}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-24 text-gray-500">暱稱:</span>
                                    <span className="text-gray-900">{inviterInfo.nickName}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-24 text-gray-500">團隊:</span>
                                    <span className="text-gray-900">{inviterInfo.teamName}</span>
                                </div>
                                <div className="flex">
                                    <span className="w-24 text-gray-500">郵箱:</span>
                                    <span className="text-gray-900">{inviterInfo.userEmail}</span>
                                </div>
                            </div>*/}
          </div>
        )}
        {errorCode !== "client.bad.invite.token" && errorCode !== "client.user.already.active" && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* 昵称字段 */}
              <FormField
                control={form.control}
                name="nickname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>昵称 *</FormLabel>
                    <FormControl>
                      <Input placeholder="请输入昵称" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 语言选择字段 */}
              <FormField
                control={form.control}
                name="lang"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>语言 *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue="" value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择语言" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ZH_HK">繁体中文(香港)</SelectItem>
                        <SelectItem value="ZH_CN">简体中文</SelectItem>
                        <SelectItem value="EN_US">英文</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 密码字段 */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>密码 *</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input placeholder="请输入密码" type={showPassword ? "text" : "password"} {...field} />
                      </FormControl>
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff className="h-5 w-5 text-gray-500" /> : <Eye className="h-5 w-5 text-gray-500" />}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                {/* <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsOpen(false)}
                                    disabled={isSubmitting}
                                >
                                    取消
                                </Button>*/}
                {!successMessage && (
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "提交中..." : "確認"}
                  </Button>
                )}
              </DialogFooter>
            </form>
          </Form>
        )}

        {/* 激活成功 */}
        {successMessage && (
          /*<div className="mb-6 p-4 bg-emerald-50\trounded-md">
                            <h4 className="text-sm font-medium text-gray-600 mb-2">激活成功! 可以访问 <a href={baseUrl} className="text-blue-600">{baseUrl}</a> 登录啦</h4>
                        </div>*/
          <Button
            style={{ height: "45px" }}
            onClick={() => {
              // 触发自定义事件来打开登录对话框
              setIsOpen(false);
              window.dispatchEvent(new CustomEvent("openLoginDialog"));
            }}
          >
            前往登录
          </Button>
        )}

        {!successMessage && (errorCode === "client.bad.invite.token" || errorCode === "client.user.already.active") && (
          <Button
            style={{ height: "45px" }}
            onClick={() => {
              setIsOpen(false);
              window.location.href = baseUrl;
            }}
          >
            返回首页
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
});
